import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import UnitImage from "common/src/tft-static/UnitImage";
import ItemImage from "common/src/tft-static/ItemImage";
import AugmentImage from "common/src/tft-static/AugmentImage";
import { PlainIcon } from "common/src/tft-static/TraitImage";
import { css } from "@emotion/css";
import React from "react";
import { ListboxComponent } from "./BaseAutocomplete";
import * as Data from "common/src/data/Data.bs";
import * as Sd from "common/src/tft-static/Sd";
import * as T2 from "common/src/bindings/Translation2";
import * as Search from "../common/Search2";
import { use as useSet } from "common/src/data/TftSet.bs";
import { make as PositionSquare } from "../player/PositionSquare.bs";

export type FU = {
  typ: "u";
  id: string;
  searchId: string;
  label: string;
};
export type FT = {
  typ: "t";
  id: string;
  searchId: string;
  rank: number;
  label: string;
};
export type FI = {
  typ: "i";
  id: string;
  searchId: string;
  label: string;
};
export type FA = {
  typ: "a";
  id: string;
  searchId: string;
  label: string;
};
export type FP = {
  typ: "p";
  id: string;
  searchId: string;
  label: string;
  places: Array<number>;
};
export type Filter = FU | FT | FI | FA | FP;

type Props = {
  options: Array<Filter>;
  onChange: (v: Filter | undefined) => void;
  value?: Filter;
  placeholder?: string;
  disabled?: boolean;
};

let unitOptions_: { [set: number]: Array<Filter> } = {};
const unitOptions = (set: number) => {
  if (unitOptions_[set]) {
    return unitOptions_[set];
  }
  const options: Array<Filter> = [];
  for (let unitId of Sd.allUnits(set)) {
    options.push({
      typ: "u",
      id: unitId,
      searchId: unitId,
      label: T2.units(unitId),
    });
  }

  unitOptions_[set] = options;
  return options;
};

let traitOptions_: { [set: number]: Array<Filter> } = {};
const traitOptions = (set: number) => {
  if (traitOptions_[set]) {
    return traitOptions_[set];
  }
  const options: Array<Filter> = [];
  for (let traitId of Sd.allTraits(set)) {
    (Sd.traitRankCounts(traitId, set) || []).forEach((count, idx) => {
      options.push({
        typ: "t",
        id: traitId,
        searchId: `${traitId}-${idx + 1}`,
        rank: idx + 1,
        label: count + " " + T2.traits(traitId),
      });
    });
  }

  traitOptions_[set] = options;
  return options;
};

let itemOptions_: { [set: number]: Array<Filter> } = {};
const itemOptions = (set: number) => {
  if (itemOptions_[set]) {
    return itemOptions_[set];
  }
  const options: Array<Filter> = [];
  for (let [id, info] of Object.entries(Data.items(set)).filter(
    ([sid, _]) => !sid.endsWith("_HR"),
  )) {
    if (info.id <= 8) {
      continue;
    }

    options.push({
      typ: "i",
      id: id,
      searchId: id,
      label: T2.items(id),
    });
  }

  itemOptions_[set] = options;
  return options;
};

let augmentOptions_: { [set: number]: Array<Filter> } = {};
const augmentOptions = (set: number) => {
  if (augmentOptions_[set]) {
    return augmentOptions_[set];
  }
  const options: Array<Filter> = [];
  for (let [id, _info] of Object.entries(Data.augments(set)).filter(
    ([_, v]) => v.typ != "hr",
  )) {
    options.push({
      typ: "a",
      id: id,
      searchId: id,
      label: T2.augments(id),
    });
  }

  augmentOptions_[set] = options;
  return options;
};

const placeLabels = [
  "1st place",
  "2nd place",
  "3rd place",
  "4th place",
  "5th place",
  "6th place",
  "7th place",
  "8th place",
];
let placeOptions_: null | Array<Filter> = null;
const placeOptions = () => {
  if (placeOptions_) {
    return placeOptions_;
  }

  const options: Array<Filter> = [];
  for (let i = 1; i <= 8; i++) {
    const label = placeLabels[i - 1];
    options.push({
      typ: "p",
      id: label,
      searchId: label,
      label: label,
      places: [i],
    });
  }

  placeOptions_ = options;
  return options;
};

const getPlaceMatches = (search: string) => {
  return new Set(placeLabels.filter((v) => v.includes(search.toLowerCase())));
};

export const getPlayerOptions = (set: number): Array<Filter> => {
  const allOptions: Array<Filter> = [];
  for (let opt of placeOptions()) {
    allOptions.push(opt);
  }
  for (let opt of unitOptions(set)) {
    allOptions.push(opt);
  }
  for (let opt of traitOptions(set)) {
    allOptions.push(opt);
  }
  for (let opt of itemOptions(set)) {
    allOptions.push(opt);
  }
  for (let opt of augmentOptions(set)) {
    allOptions.push(opt);
  }

  return allOptions;
};

export const getCompsOptions = (set: number): Array<Filter> => {
  const allOptions: Array<Filter> = [];
  for (let opt of unitOptions(set)) {
    allOptions.push(opt);
  }
  for (let opt of traitOptions(set)) {
    allOptions.push(opt);
  }

  return allOptions;
};

const FullSearch = ({
  onChange,
  placeholder = undefined,
  disabled = false,
  options,
}: Props) => {
  const set = useSet();
  const getMatches = React.useCallback((search: string) => {
    if (search) {
      let unitMatches = Search.searchUnitsExact(search, set);
      let traitMatches = Search.searchRankTraits(search, set);
      let itemMatches = Search.searchItems(search, set);
      let augMatches = Search.searchAugments(search, set);
      let placeMatches = getPlaceMatches(search);
      return options.filter(
        (o) =>
          unitMatches.has(o.searchId) ||
          traitMatches.has(o.searchId) ||
          itemMatches.has(o.searchId) ||
          augMatches.has(o.searchId) ||
          placeMatches.has(o.searchId),
      );
    } else {
      return options;
    }
  }, []);
  const renderOption = React.useCallback((props: any, v: Filter) => {
    let el = null;
    if (v.typ === "u") {
      el = <UnitImage unitId={v.id} size={28} />;
    } else if (v.typ === "t") {
      el = <PlainIcon traitId={v.id} size={28} rank={v.rank} />;
    } else if (v.typ === "i") {
      el = <ItemImage itemId={v.id} size={28} rounded="rounded" />;
    } else if (v.typ === "a") {
      el = <AugmentImage augId={v.id} size={28} />;
    } else if (v.typ === "p") {
      el = <PositionSquare pl={v.places[0]} size={26} queueId={1100} />;
    }

    return (
      <div key={v.label} {...props}>
        {el}
        <span className="font-montserrat leading-tight font-medium inline text-base pl-[6px]">
          {v.label}
        </span>
      </div>
    );
  }, []);

  const [value, setValue] = React.useState<null | undefined>(null);

  return (
    <Autocomplete
      disabled={disabled}
      sx={{ width: 200 }}
      classes={{
        popper: css({ minWidth: "300px" }),
      }}
      value={value}
      onChange={(_, v: Filter | null) => {
        if (v) {
          setValue((v) => (v === null ? undefined : null));
          onChange(v || undefined);
        }
      }}
      filterOptions={(vs, state) => {
        if (state.inputValue) {
          const filters = getMatches(state.inputValue);
          return filters;
        } else {
          return vs;
        }
      }}
      // @ts-ignore
      ListboxComponent={ListboxComponent}
      options={options}
      renderOption={renderOption}
      autoHighlight
      renderInput={(params) => {
        return (
          <TextField
            placeholder={placeholder}
            {...params}
            inputProps={{
              ...params.inputProps,
              // @ts-ignore
              value: params.inputProps.value
                ? // @ts-ignore
                  params.inputProps.value
                : // : value?.label || "",
                  "",
            }}
            variant="standard"
            InputProps={{
              ...params.InputProps,
              classes: {
                input: "font-montserrat font-medium",
              },
              startAdornment: null,
              // <InputAdornment position="start">
              //   {value && startAdornment(value)}
              // </InputAdornment>
            }}
          />
        );
      }}
    />
  );
};

export default FullSearch;
